import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Battery, HardDrive, Brain, ArrowRight, Zap, Shield, TrendingUp, Sparkles, Star } from 'lucide-react';
import { mockData } from './mock';
import { useScrollAnimation, createScrollAnimationClasses } from '../hooks/useScrollAnimation';

const iconComponents = {
  Battery,
  HardDrive,
  Brain
};

export const NewFeaturesSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ delay: 0 });
  const [cardsRef, cardsVisible] = useScrollAnimation({ delay: 200 });
  const [statsRef, statsVisible] = useScrollAnimation({ delay: 400 });

  return (
    <section id="solutions" className="py-24 bg-gradient-to-br from-gray-50 via-white to-blue-50/30 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-200/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-green-200/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div ref={headerRef} className={`text-center mb-20 ${createScrollAnimationClasses('fadeInUp', headerVisible)}`}>
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-100 to-emerald-100 px-6 py-3 rounded-full mb-6 border border-green-200/50">
            <Sparkles size={16} className="text-green-600" />
            <Badge className="bg-transparent text-green-700 px-0 py-0 text-sm font-semibold">
              Innovation & Future
            </Badge>
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Next-Generation
            <span 
              className="block bg-clip-text text-transparent"
              style={{ 
                backgroundImage: 'linear-gradient(to right, rgb(6 47 136), rgb(147 51 234))' 
              }}
            >
              Smart Solutions
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Pioneering the future of urban infrastructure with cutting-edge energy management, 
            storage systems, and artificial intelligence integration that transforms cities into 
            intelligent, sustainable ecosystems.
          </p>
        </div>

        {/* Features Grid */}
        <div ref={cardsRef} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20 ${createScrollAnimationClasses('fadeInUp', cardsVisible)}`}>
          {mockData.newFeatures.map((feature, index) => {
            const IconComponent = iconComponents[feature.icon];
            const gradients = [
              'from-blue-500 to-indigo-600',
              'from-green-500 to-teal-600', 
              'from-purple-500 to-pink-600'
            ];
            const cardGradients = [
              'from-blue-50/80 to-indigo-50/80',
              'from-green-50/80 to-teal-50/80',
              'from-purple-50/80 to-pink-50/80'
            ];
            
            return (
              <Card key={feature.id} className="group relative overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 bg-white/90 backdrop-blur-sm">
                {/* Background Gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${cardGradients[index]} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                
                {/* Floating Elements */}
                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <Star size={16} className="text-yellow-400 animate-pulse" />
                </div>
                
                <CardHeader className="relative z-10 pb-4 p-8">
                  <div className={`w-20 h-20 bg-gradient-to-br ${gradients[index]} rounded-3xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 shadow-lg`}>
                    <IconComponent size={36} className="text-white" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors">
                    {feature.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="relative z-10 p-8 pt-0">
                  <p className="text-gray-600 mb-8 leading-relaxed text-lg">
                    {feature.description}
                  </p>
                  
                  {/* Benefits */}
                  <div className="space-y-4 mb-8">
                    {feature.benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center text-gray-700 group/benefit">
                        <div className="w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mr-4 group-hover/benefit:scale-125 transition-transform duration-300"></div>
                        <span className="font-semibold text-base">{benefit}</span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    size="lg"
                    variant="ghost" 
                    className="w-full justify-between group-hover:bg-white/90 group-hover:text-blue-600 transition-all duration-300 border border-gray-200 group-hover:border-blue-200 rounded-xl py-4"
                  >
                    <span className="font-semibold">Explore Technology</span>
                    <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform duration-300" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Innovation Stats */}
        <div ref={statsRef} className={`bg-white/90 backdrop-blur-sm rounded-3xl p-12 shadow-2xl border border-gray-100/50 ${createScrollAnimationClasses('fadeInUp', statsVisible)}`}>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-purple-100 px-6 py-3 rounded-full mb-6">
              <TrendingUp size={16} className="text-blue-600" />
              <Badge className="bg-transparent text-blue-700 px-0 py-0 text-sm font-semibold">
                Performance Metrics
              </Badge>
            </div>
            <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Driving Innovation Forward
            </h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our cutting-edge solutions deliver measurable results that transform urban infrastructure.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Zap size={28} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">90%</div>
              <div className="text-gray-600 font-semibold text-lg">Maximum Energy Reduction</div>
              <div className="text-sm text-gray-500 mt-2">Through intelligent algorithms</div>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <Shield size={28} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors">24/7</div>
              <div className="text-gray-600 font-semibold text-lg">System Reliability</div>
              <div className="text-sm text-gray-500 mt-2">Uninterrupted operations</div>
            </div>
            
            <div className="text-center group">
              <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                <TrendingUp size={28} className="text-white" />
              </div>
              <div className="text-4xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">$1M</div>
              <div className="text-gray-600 font-semibold text-lg">Annual R&D Investment</div>
              <div className="text-sm text-gray-500 mt-2">Continuous innovation</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};